import { useMemo, useContext, useState, useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import {
  Flex,
  Text,
  Button,
  useColorModeValue,
  FormControl,
  FormLabel,
  Input,
  Select,
  useToast,
  SimpleGrid,
  Heading,
  FormErrorMessage,
  Box,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react';

import Card from 'components/card/Card';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  apiCreateLead,
  apiUpdateLead,
  apiGetLeadSources,
  apiGetUserWorkflows,
  apiGetWorkflowUsers,
  apiGetLeadTags,
  apiLeadOverview,
  apiCreateLeadTag,
} from 'api';

import {
  LEAD_EDIT_STATUSES,
  LEAD_STATUS_NEW,
  LEAD_STATUS_ARCHIVED,
} from 'views/leads/constants';

import { ROLE_ADMIN, ROLE_MANAGER } from 'variables/constants';

import { PROVINCES, LEAD_TYPE_OPTIONS } from 'variables/options';

import type {
  APILeadSourceData,
  APILeadWorkflowData,
  APIUserData,
  APILeadTagData,
  APILeadOverviewData,
} from 'types/api.types';

import MultiSelect from 'components/fields/MultiSelect';
import SelectSearch from 'components/fields/SelectSearch';

import { AuthContext } from 'contexts/AuthContext';

import { useViewAs } from 'hooks/useViewAs';

import LeadStatusChange from 'views/leads/components/LeadStatusChange';
import UnarchiveLead from 'views/leads/components/UnarchiveLead';

const LeadFormView = () => {
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const viewAs = useViewAs();
  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [newTagName, setNewTagName] = useState('');
  const [tagCreationError, setTagCreationError] = useState<string[]>([]);

  const {
    data: lead,
    isLoading: isLoadingLead,
    error: leadError,
    isFetching: isFetchingLead,
    refetch: refetchLead,
  } = useQuery({
    queryKey: ['lead-overview', id, viewAs.filterParams],
    queryFn: () =>
      apiLeadOverview(parseInt(id), viewAs.viewAsEmail, viewAs.viewAsBranchId),
    enabled: !!id, // Only run the query if id exists
  }) as {
    data: APILeadOverviewData | undefined;
    isLoading: boolean;
    isError: boolean;
    isFetching: boolean;
    error: any;
    refetch: () => void;
  };

  const isEditMode = !!lead;

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    postal_code: '',
    source: '',
    value: '',
    tags: [] as number[],
    province: '',
    workflow: '',
    user: '',
    status: LEAD_STATUS_NEW,
    type: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (lead) {
      setFormData({
        first_name: lead.first_name || '',
        last_name: lead.last_name || '',
        email: lead.email || '',
        phone_number: lead.phone_number || '',
        postal_code: lead.postal_code || '',
        source: lead.source?.id?.toString() || '',
        value: lead.value?.toString() || '',
        tags: lead.tags?.map((tag) => tag.id) || [],
        province: lead.province_display || '',
        workflow: lead.workflow?.id?.toString() || '',
        user: lead.user?.id?.toString() || '',
        status: lead.status || LEAD_STATUS_NEW,
        type: lead.type?.toString() || '',
      });

      setErrors({});
    }
  }, [lead]);

  // Fetch sources from API
  const { data: sources = [], isLoading: loadingSources } = useQuery<
    APILeadSourceData[]
  >({
    queryKey: ['lead-sources'],
    queryFn: apiGetLeadSources,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch workflows from API
  const { data: workflows = [], isLoading: loadingWorkflows } = useQuery<
    APILeadWorkflowData[]
  >({
    queryKey: ['user-workflows'],
    queryFn: apiGetUserWorkflows,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch tags from API
  const { data: availableTags = [], isLoading: loadingTags } = useQuery<
    APILeadTagData[]
  >({
    queryKey: ['lead-tags'],
    queryFn: () => apiGetLeadTags(null, 'all'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch users for selected workflow
  const {
    data: workflowUsers = [],
    isLoading: loadingWorkflowUsers,
    isFetching: isFetchingWorkflowUsers,
  } = useQuery<APIUserData[]>({
    queryKey: ['lead-users', parseInt(formData.workflow)],
    queryFn: () => apiGetWorkflowUsers(parseInt(formData.workflow)),
    enabled: !!formData.workflow,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Create or update mutation based on isEditMode
  const leadMutation = useMutation({
    mutationFn: (data: any) =>
      isEditMode
        ? apiUpdateLead(parseInt(id) || lead.id, data)
        : apiCreateLead(data),
    onSuccess: (data) => {
      toast({
        title: isEditMode
          ? 'Lead updated successfully'
          : 'Lead created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      navigate(`/admin/leads/${data.id}`);
    },
    onError: (error: any) => {
      toast({
        title: isEditMode ? 'Error updating lead' : 'Error creating lead',
        description: 'Please check the form for errors',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      const formattedErrors: Record<string, string> = {};

      Object.entries(error).forEach(([field, messages]) => {
        if (Array.isArray(messages) && messages.length > 0) {
          formattedErrors[field] = messages[0];
        } else if (typeof messages === 'string') {
          formattedErrors[field] = messages;
        }
      });

      setErrors(formattedErrors);
    },
  });

  // Create tag mutation
  const createTagMutation = useMutation({
    mutationFn: (name: string) => apiCreateLeadTag({ name, user_id: user.id }),
    onSuccess: (data) => {
      toast({
        title: 'Tag created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      // Add the new tag to formData
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, data.id],
      }));

      // Invalidate and refetch tags
      queryClient.invalidateQueries({ queryKey: ['lead-tags'] });

      // Close modal and reset
      onClose();
      setNewTagName('');
      setTagCreationError([]);
    },
    onError: (error: any) => {
      if (error.name && Array.isArray(error.name)) {
        setTagCreationError(error.name);
      } else {
        setTagCreationError(['Failed to create tag']);
      }
    },
  });

  const availableTagsIds = useMemo(() => {
    return availableTags.map((tag) => tag.id);
  }, [availableTags]);

  const handleCreateTag = () => {
    setTagCreationError([]);
    createTagMutation.mutate(newTagName);
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      value: parseFloat(formData.value) || 0,
      user: formData.user ? parseInt(formData.user) : null,
      workflow: formData.workflow ? parseInt(formData.workflow) : null,
      source: formData.source ? parseInt(formData.source) : null,
    };

    leadMutation.mutate(submitData);
  };

  const handleTagsChange = (newTags: number[]) => {
    setFormData((prev) => ({
      ...prev,
      tags: newTags,
    }));
  };

  const canEditData = useMemo(() => {
    if (!lead) return true;
    if ([ROLE_ADMIN, ROLE_MANAGER].includes(user.role)) return true;
    if (!lead.user) return false;
    if (lead.status === LEAD_STATUS_ARCHIVED) return false;
    return lead.user.id === user.id;
  }, [lead, user.id]);

  if (isLoadingLead || isFetchingLead) {
    return (
      <Card>
        <Flex align="center" justify="center">
          <Spinner size="sm" mr="10px" /> Loading lead data...
        </Flex>
      </Card>
    );
  }

  if (!canEditData) {
    return (
      <Card>
        <Flex align="center" justify="center">
          You are not allowed to edit this lead. Please contact support. Lead
          ID: {id}
        </Flex>
      </Card>
    );
  }

  if (leadError) {
    return (
      <Box p="20px" pb="0">
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{JSON.stringify(leadError)}</AlertDescription>
        </Alert>
      </Box>
    );
  }

  if (lead?.status === LEAD_STATUS_ARCHIVED) {
    return (
      <Card>
        <Flex align="center" justify="center" gap="20px">
          <Box>This lead is deleted. You need to restore it to edit.</Box>
          <UnarchiveLead lead={lead} onSuccess={refetchLead} />
        </Flex>
      </Card>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <Card mb="20px">
        <Heading size="md" mb={6} color={textColor}>
          {isEditMode ? 'Edit Lead' : 'Create New Lead'}
        </Heading>

        {errors.non_field_errors && (
          <Box mb={6} p={3} borderRadius="md" bg="red.100" color="red.700">
            {errors.non_field_errors}
          </Box>
        )}

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <FormControl isRequired isInvalid={!!errors.first_name}>
            <FormLabel>First Name</FormLabel>
            <Input
              variant="main"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              placeholder="First name"
            />
            <FormErrorMessage>{errors.first_name}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.last_name}>
            <FormLabel>Last Name</FormLabel>
            <Input
              variant="main"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              placeholder="Last name"
            />
            <FormErrorMessage>{errors.last_name}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.email}>
            <FormLabel>E-mail Address</FormLabel>
            <Input
              variant="main"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="email@example.com"
            />
            <FormErrorMessage>{errors.email}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.phone_number}>
            <FormLabel>Phone Number</FormLabel>
            <Input
              variant="main"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              placeholder="(123) 456-7890"
            />
            <FormErrorMessage>{errors.phone_number}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.postal_code}>
            <FormLabel>Postal Code</FormLabel>
            <Input
              variant="main"
              name="postal_code"
              value={formData.postal_code}
              onChange={handleChange}
              placeholder="A1A 1A1"
            />
            <FormErrorMessage>{errors.postal_code}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.province}>
            <FormLabel>Province</FormLabel>
            <Select
              variant="main"
              name="province"
              value={formData.province}
              onChange={handleChange}
              placeholder="Select province"
            >
              {PROVINCES.map((province) => (
                <option key={province.value} value={province.value}>
                  {province.label}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.province}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.source}>
            <FormLabel>Source</FormLabel>
            <SelectSearch
              options={sources?.map((source) => ({
                value: source.id.toString(),
                label: source.name,
                isDisabled: !source.is_selectable,
              }))}
              value={formData.source}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  source: value.toString(),
                }))
              }
              placeholder="All sources"
              isDisabled={
                loadingSources ||
                (id && !!formData.source && user.role !== ROLE_ADMIN)
              }
            />
            <FormErrorMessage>{errors.source}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.value || !!errors.value}>
            <FormLabel>Premium ($)</FormLabel>
            <Input
              variant="main"
              name="value"
              type="number"
              value={formData.value}
              onChange={handleChange}
              placeholder="0.00"
            />
            <FormErrorMessage>{errors.value || errors.value}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.workflow}>
            <FormLabel>Workflow</FormLabel>
            <SelectSearch
              options={workflows.map((workflow) => ({
                value: workflow.id.toString(),
                label: workflow.name,
                isDisabled: !workflow.is_selectable && user.role !== ROLE_ADMIN,
              }))}
              value={formData.workflow}
              onChange={(value) => {
                setFormData((prev) => ({
                  ...prev,
                  workflow: value.toString(),
                  user: '', // Reset user when workflow changes
                }));
              }}
              placeholder="All workflows"
              isDisabled={
                loadingWorkflows ||
                (user.role !== ROLE_ADMIN &&
                  formData.workflow &&
                  !workflows.find((w) => w.id.toString() === formData.workflow)
                    ?.is_selectable)
              }
            />
            <FormErrorMessage>{errors.workflow}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.user}>
            <FormLabel>Assign to User</FormLabel>
            {!isFetchingWorkflowUsers ? (
              <>
                <SelectSearch
                  options={workflowUsers.map((user) => ({
                    value: user.id.toString(),
                    label: `${user.first_name} ${user.last_name}`,
                  }))}
                  value={formData.user}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      user: value.toString(),
                    }))
                  }
                  placeholder={
                    !formData.workflow
                      ? 'Select a workflow first'
                      : 'Unassigned'
                  }
                  isDisabled={
                    loadingWorkflowUsers ||
                    isFetchingWorkflowUsers ||
                    !formData.workflow
                  }
                />
                {!formData.workflow && (
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    Please select a workflow to see available users
                  </Text>
                )}
              </>
            ) : (
              <Flex align="center" minH="40px">
                <Spinner size="sm" mr="10px" /> Loading...
              </Flex>
            )}
            <FormErrorMessage>{errors.user}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.status}>
            <FormLabel>Status</FormLabel>

            {lead ? (
              <LeadStatusChange lead={lead} />
            ) : (
              <Select
                variant="main"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                {LEAD_EDIT_STATUSES.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </Select>
            )}
            <FormErrorMessage>{errors.status}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.tags}>
            <FormLabel>Tags</FormLabel>
            <Flex gap={2}>
              <Box w="80%">
                <MultiSelect
                  selectedIds={formData.tags.map((tag) => ({
                    id: tag,
                    // Tags added by other users or not part of the branch can't be removed
                    canRemove: availableTagsIds.includes(tag),
                  }))}
                  availableItems={availableTags}
                  isLoading={loadingTags}
                  onChange={handleTagsChange}
                  label="Select tags"
                  itemLabelSingular="tag"
                  itemLabelPlural="tags"
                />
              </Box>
              <Box w="20%">
                <Button w="100%" variant="outline" onClick={onOpen}>
                  Add new
                </Button>
              </Box>
            </Flex>
            <FormErrorMessage>{errors.tags}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.type}>
            <FormLabel>Type</FormLabel>
            <Select
              variant="main"
              name="type"
              value={formData.type}
              onChange={handleChange}
              placeholder="Select type"
            >
              {LEAD_TYPE_OPTIONS.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.type}</FormErrorMessage>
          </FormControl>
        </SimpleGrid>

        <Flex mt={10} justify="flex-end">
          <Button
            variant="outline"
            mr={3}
            onClick={() =>
              navigate(
                isEditMode
                  ? `/admin/leads/${id || lead.id}`
                  : '/admin/leads/list',
              )
            }
          >
            Cancel
          </Button>
          <Button
            variant="brand"
            type="submit"
            isLoading={leadMutation.isPending}
            loadingText={isEditMode ? 'Updating' : 'Creating'}
          >
            {isEditMode ? 'Update Lead' : 'Create Lead'}
          </Button>
        </Flex>
      </Card>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Tag</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isInvalid={tagCreationError.length > 0}>
              <FormLabel>Tag Name</FormLabel>
              <Input
                variant="main"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="Enter tag name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateTag();
                  }
                }}
              />
              {tagCreationError.length > 0 && (
                <FormErrorMessage>
                  {tagCreationError.map((err, idx) => (
                    <div key={idx}>{err}</div>
                  ))}
                </FormErrorMessage>
              )}
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateTag}
              isLoading={createTagMutation.isPending}
              loadingText="Creating"
              isDisabled={!newTagName.trim()}
            >
              Create Tag
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </form>
  );
};

export default LeadFormView;
